/**
 * Pricing Redux Sagas
 * Side effects for user-triggered actions
 *
 * Listens for auth events to automatically load/clear pricing profile
 */

import { call, put, takeLatest, type PayloadAction } from '@spcc/core'
import { pricingApi } from '../infrastructure/api'
import { pricingActions } from './pricing.slice'
import type { PricedProduct, PricingProfile } from '../domain/types'

// Auth action types for cross-domain listening
const AUTH_LOGIN_SUCCESS = 'auth/loginSuccess'
const AUTH_INIT_SUCCESS = 'auth/initializeAuthSuccess'
const AUTH_LOGOUT = 'auth/logout'

/**
 * Fetch profile saga
 * Gets user's pricing context (tenant, customer group, price list)
 */
function* fetchProfileSaga() {
  try {
    const profile: PricingProfile = yield call(pricingApi.getProfile)
    yield put(pricingActions.fetchProfileSuccess(profile))
  } catch (error) {
    yield put(pricingActions.fetchProfileFailure((error as Error).message))
  }
}

/**
 * Handle auth success - automatically load pricing profile
 * Triggered on login success or session restoration
 */
function* onAuthSuccessSaga() {
  try {
    const profile: PricingProfile = yield call(pricingApi.getProfile)
    yield put(pricingActions.fetchProfileSuccess(profile))
  } catch (error) {
    // Silently fail - profile is optional for pricing to work
    // Backend still resolves prices correctly based on auth token
    console.warn('[Pricing] Failed to load profile after auth:', (error as Error).message)
  }
}

/**
 * Handle logout - clear pricing state
 */
function* onLogoutSaga() {
  yield put(pricingActions.clearProfile())
  yield put(pricingActions.clearPricing())
}

/**
 * Fetch products saga
 * Gets products with prices based on user's profile or default
 */
function* fetchProductsSaga(
  action: PayloadAction<{ categoryCode?: string; search?: string; locale?: string } | undefined>
) {
  try {
    const params = action.payload ?? {}
    const products: PricedProduct[] = yield call(pricingApi.getProducts, params)
    yield put(pricingActions.fetchProductsSuccess(products))
  } catch (error) {
    yield put(pricingActions.fetchProductsFailure((error as Error).message))
  }
}

/**
 * Fetch single product saga
 */
function* fetchProductSaga(action: PayloadAction<string>) {
  try {
    const product: PricedProduct | null = yield call(
      pricingApi.getProduct,
      action.payload
    )
    yield put(pricingActions.fetchProductSuccess(product))
  } catch (error) {
    yield put(pricingActions.fetchProductFailure((error as Error).message))
  }
}

/**
 * Search products saga
 */
function* searchProductsSaga(action: PayloadAction<string>) {
  try {
    const products: PricedProduct[] = yield call(
      pricingApi.searchProducts,
      action.payload
    )
    yield put(pricingActions.fetchProductsSuccess(products))
  } catch (error) {
    yield put(pricingActions.fetchProductsFailure((error as Error).message))
  }
}

/**
 * Filter by category saga
 */
function* filterByCategorySaga(action: PayloadAction<string>) {
  try {
    const products: PricedProduct[] = yield call(
      pricingApi.getProductsByCategory,
      action.payload
    )
    yield put(pricingActions.fetchProductsSuccess(products))
  } catch (error) {
    yield put(pricingActions.fetchProductsFailure((error as Error).message))
  }
}

/**
 * Root pricing saga
 * Combines all pricing-related sagas
 *
 * Listens for:
 * - Pricing domain actions (fetchProfile, fetchProducts, etc.)
 * - Auth domain events (login, init, logout) for cross-domain integration
 */
export function* pricingSaga() {
  // Pricing domain actions
  yield takeLatest(pricingActions.fetchProfile.type, fetchProfileSaga)
  yield takeLatest(pricingActions.fetchProducts.type, fetchProductsSaga)
  yield takeLatest(pricingActions.fetchProduct.type, fetchProductSaga)
  yield takeLatest(pricingActions.searchProducts.type, searchProductsSaga)
  yield takeLatest(pricingActions.filterByCategory.type, filterByCategorySaga)

  // Cross-domain: Listen for auth events to auto-load/clear pricing profile
  yield takeLatest(AUTH_LOGIN_SUCCESS, onAuthSuccessSaga)
  yield takeLatest(AUTH_INIT_SUCCESS, onAuthSuccessSaga)
  yield takeLatest(AUTH_LOGOUT, onLogoutSaga)
}
